import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import './Bubbles.css';
import { CommonContextMenu } from '../commonContextMenu/commonContextMenu';
import { AppContext } from '../../appContext';

interface IBubblesProps{
    points: Array<{label: string, value: number}>;
    onSelect: (word: string) => void;
    onDelete?: (word: string) => void;
    layout?: 'default' | 'packed';
}

// Packed layout algorithm - spreads bubbles evenly across container
const getPackedPositions = (data: Array<number>, outsize: {x: number, y: number}) => {
    if (data.length === 0) return [];
    
    const gap = 6;
    const minRadius = 18;
    const maxRadius = Math.min(outsize.x, outsize.y) * 0.32;
    
    // Create indexed data and sort by value descending (place largest first)
    const indexedData = data.map((value, originalIndex) => ({ value, originalIndex }));
    indexedData.sort((a, b) => b.value - a.value);
    
    // Normalize values to radii using sqrt for area-proportional sizing
    const maxValue = Math.max(...data);
    const minValue = Math.min(...data);
    const valueRange = maxValue - minValue || 1;
    
    const radii = data.map(value => {
        const normalized = (value - minValue) / valueRange;
        return minRadius + Math.sqrt(normalized) * (maxRadius - minRadius);
    });
    
    // Positions stored as centers (cx, cy)
    const centers: Array<{cx: number, cy: number}> = [];
    const centerX = outsize.x / 2;
    const centerY = outsize.y / 2;
    
    // Check overlap using center-to-center distance
    const checkOverlap = (cx: number, cy: number, radius: number) => {
        for (let i = 0; i < centers.length; i++) {
            const dx = cx - centers[i].cx;
            const dy = cy - centers[i].cy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const placedRadius = radii[indexedData[i].originalIndex];
            if (dist < radius + placedRadius + gap) {
                return true;
            }
        }
        return false;
    };
    
    // Check if circle is within bounds
    const isInBounds = (cx: number, cy: number, radius: number) => {
        return cx - radius >= 0 && 
               cy - radius >= 0 && 
               cx + radius <= outsize.x && 
               cy + radius <= outsize.y;
    };
    
    // Place circles in order of size (largest first)
    for (let i = 0; i < indexedData.length; i++) {
        const radius = radii[indexedData[i].originalIndex];
        let placed = false;
        
        if (i === 0) {
            // Place first (largest) circle in center
            centers.push({ cx: centerX, cy: centerY });
            placed = true;
        } else {
            // Try spiral pattern from center
            let angle = 0;
            let spiralRadius = gap;
            const maxSpiral = Math.max(outsize.x, outsize.y);
            const angleStep = 0.15;
            
            while (!placed && spiralRadius < maxSpiral) {
                const cx = centerX + Math.cos(angle) * spiralRadius;
                const cy = centerY + Math.sin(angle) * spiralRadius;
                
                if (isInBounds(cx, cy, radius) && !checkOverlap(cx, cy, radius)) {
                    centers.push({ cx, cy });
                    placed = true;
                }
                
                angle += angleStep;
                if (angle >= Math.PI * 2) {
                    angle = 0;
                    spiralRadius += minRadius * 0.4;
                }
            }
        }
        
        // If still not placed, find any valid position
        if (!placed) {
            for (let tryX = radius; tryX < outsize.x - radius; tryX += radius) {
                for (let tryY = radius; tryY < outsize.y - radius; tryY += radius) {
                    if (!checkOverlap(tryX, tryY, radius)) {
                        centers.push({ cx: tryX, cy: tryY });
                        placed = true;
                        break;
                    }
                }
                if (placed) break;
            }
        }
        
        // Last resort - place outside visible area (will be clipped)
        if (!placed) {
            centers.push({ cx: -radius * 2, cy: -radius * 2 });
        }
    }
    
    // Convert back to original order and top-left positions
    const result = new Array(data.length);
    for (let i = 0; i < indexedData.length; i++) {
        const originalIndex = indexedData[i].originalIndex;
        const radius = radii[originalIndex];
        result[originalIndex] = {
            radius: radius * 2,
            position: {
                x: centers[i].cx - radius,
                y: centers[i].cy - radius
            }
        };
    }
    
    return result;
}

const getThirdPoint = (r1: number, r2: number, r3:number, pos1: {x: number, y: number}, pos2: {x: number, y: number})=>{
    const lens = {
        a: Math.hypot(pos1.x - pos2.x, pos1.y - pos2.y),
        b: r1 + r3,
        c: r2 + r3
    }
    if (lens.a >= lens.b + lens.c){
        return [];
    }
    const angA = Math.acos(((lens.a ** 2) + (lens.b ** 2) - (lens.c ** 2)) / (2 * lens.a * lens.b));

    //const atan = Math.atan2(pos2.x - pos1.x, pos2.y - pos1.y);
    const atan = Math.atan2(pos2.y - pos1.y, pos2.x - pos1.x );
    const x1 = pos1.x + lens.b * Math.cos(atan + angA);
    const y1 = pos1.y + lens.b * Math.sin(atan + angA);
    const x2 = pos1.x + lens.b * Math.cos(atan - angA);
    const y2 = pos1.y + lens.b * Math.sin(atan - angA);
    return [{x: x1, y: y1}, {x: x2, y: y2}];
}

const getBounds = (positions: Array<{x: number, y: number}>, sizes: Array<number>)=>{
    let minX = Number.MAX_SAFE_INTEGER;
    positions.forEach((it, i)=>{
        if (minX>it.x){
            minX = it.x;
        }
    });
    let minY = Number.MAX_SAFE_INTEGER;
    positions.forEach((it, i)=>{
        if (minY>it.y){
            minY = it.y;
        }
    });

    let maxX = -Number.MAX_SAFE_INTEGER;
    positions.forEach((it, i)=>{
        if (maxX<it.x + sizes[i]){
            maxX = it.x + sizes[i];
        }
    });

    let maxY = -Number.MAX_SAFE_INTEGER;
    positions.forEach((it, i)=>{
        if (maxY<it.y + sizes[i]){
            maxY = it.y + sizes[i];
        }
    });
    return {
        minX, minY, maxX, maxY
    }
}

const to2signs = (v: number)=>{
    return v.toString(16).length == 1 ? '0'+v.toString(16) : v.toString(16);
}

const genColor = ()=>{
    let nc = 128 * 2;
    const R = Math.floor(Math.random() * 128 + Math.min(nc / 2, 128));
    nc -= R;
    const G = Math.floor(Math.random() * 128 + Math.min(nc / 2, 128));
    nc -= G;
    const B = Math.floor(Math.random() * 128 + Math.min(nc / 2, 128));
    return `#${to2signs(R)}${to2signs(G)}${to2signs(B)}`;
}

const findPoints = (pi1: number, pi2: number, pi3:number, positions: Array<{x: number, y: number}>, data: Array<number>)=>{
    const i = pi3;
    const newRadius = data[i]/2;
    const np = getThirdPoint(data[pi2] /2, data[pi1]/2, newRadius, 
        {   
            x: positions[pi2].x + data[pi2] /2,
            y: positions[pi2].y + data[pi2] /2,
        },
        {   
            x: positions[pi1].x + data[pi1] /2,
            y: positions[pi1].y + data[pi1] /2,
        }
    );
    if (np.length == 0){
        return;
    }
    //console.log(np);
    const sp = np[0];
    const nextPos = {
        x: sp.x - newRadius,
        y: sp.y - newRadius,
    };
    const sp2 = np[0];
    const nextPos1 = {
        x: sp2.x - newRadius,
        y: sp2.y - newRadius,
    };
    const results = [];

    const isCorrect = (pos: {x: number, y: number})=> {
        return positions.find((p, pi)=>{
            return pi!=i 
                && (Math.hypot(
                        (p.x + data[pi] /2) - (pos.x + newRadius), 
                        (p.y + data[pi] /2) - (pos.y + newRadius)
                    ) < 
                    ((data[pi]) / 2 + newRadius - 0.1)
                ) 
        }) == undefined;
    };

    if (isCorrect(nextPos)){
        //positions.push(nextPos);  
        results.push(nextPos)  
    } else if (isCorrect(nextPos1)) {
        results.push(nextPos1) 
    } else {
        //console.log('wrong')
    }
    return results;
}

const getNextPosition = (data: Array<number>, positions: Array<{x: number, y: number}>, i: number, outsize: {x: number, y: number}) => {
    //const positions: Array<{x: number, y: number}> = [];
    let optimal: any = {
        i1: 0,
        i2: 0,
        v: Number.MAX_SAFE_INTEGER,
        p: null
    };
    positions.forEach((ft, fi) => {
        positions.forEach((st, si) => {
            const points = findPoints(fi, si, i, positions, data);
            points && points.length && points.forEach(point=>{
            if (point && !Number.isNaN(point.x)){
                const bounds = getBounds([...positions, point], data);
                const boundArea = (bounds.maxX - bounds.minX) * (bounds.maxY - bounds.minY);
                const scaler = Math.max((bounds.maxX - bounds.minX) / outsize.x, (bounds.maxY - bounds.minY) / outsize.y);
                const v = scaler
                if (v<optimal.v) {
                    optimal.i1 = fi;
                    optimal.i2 = si;
                    optimal.v = v;
                    optimal.p = point;
                }
            }});
        });
    });
    //positions.push(optimal.p);
    return optimal.p;
}

const getPositions = (data: Array<number>, outsize:{x: number, y: number})=>{
    const positions: Array<{x: number, y: number}> = [];
    data.forEach((it, i)=>{
        if (positions.length == 0){
            positions.push({x: Math.random() * (outsize.x - it), y: Math.random() * (outsize.y - it)});
        } else if (positions.length == 1){
            const last = positions[i-1];
            const lastSize = data[i-1];
            const offsetVector = {x: lastSize / 2 + it / 2, y:0}
            const angle = Math.random() * Math.PI * 2;
            const position = {
                x: (last.x + lastSize / 2 - it / 2) + Math.sin(angle) * offsetVector.x,
                y: (last.y + lastSize / 2 - it / 2) + Math.cos(angle) * offsetVector.x
            }
            positions.push(position);
        } else {
            const position = getNextPosition(data, positions, i, outsize);
            positions.push(position);
        }
    });

    //scaling positions
    const bounds = getBounds(positions, data);
    const scaler = Math.max((bounds.maxX - bounds.minX) / outsize.x, (bounds.maxY - bounds.minY) / outsize.y);
    positions.forEach(it=>{
        it.x = (it.x - bounds.minX) / scaler + 2;
        it.y = (it.y - bounds.minY) / scaler + 2;
    });
    const gap = 5;
    
    // Calculate actual width of scaled bubbles and center horizontally
    const scaledBounds = getBounds(positions, data.map(d => d / scaler));
    const actualWidth = scaledBounds.maxX - scaledBounds.minX;
    const horizontalOffset = (outsize.x - actualWidth) / 2 - scaledBounds.minX;
    
    const resultData = data.map((dataItem, dataItemIndex) => {
        return {
            radius: dataItem / scaler - gap,
            position: {
                x: positions[dataItemIndex].x + horizontalOffset,
                y: positions[dataItemIndex].y
            }
        }
    });
    return resultData;
}

const AutoSizedText = ({width, text, className} : {width: number, text: React.ReactNode, className: string})=>{
    const ref = useRef<HTMLDivElement>(null);
    const refOriginal = useRef<HTMLDivElement>(null);
    const [scaler, setScaler] = useState(0);
    useEffect(()=>{
        if (!ref.current || !refOriginal.current){
            return;
        }
        console.log('scaler', (width) / refOriginal.current.getBoundingClientRect().width);
        setScaler((width) / refOriginal.current.getBoundingClientRect().width);
    }, [ref.current, refOriginal.current, text, width, className]);
    return <div ref={ref} className={className}>
        <div style={{transform: `scale(${scaler})`}}>
            {text}  
        </div>
        <div ref={refOriginal} style={{position: 'absolute', pointerEvents:'none', opacity:0}}>
            <div >
                {text}  
            </div>
        </div>
    </div>
}

export const Bubbles = ({points, onSelect, onDelete, layout = 'default'}: IBubblesProps)=>{
    const { translate } = useContext(AppContext);
    const [contextMenu, setContextMenu] = useState<{position: {top: number, left: number}, wordIndex: number} | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({x: 400, y: 280});
    
    useEffect(() => {
        if (!containerRef.current) return;
        
        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    setContainerSize({x: rect.width, y: rect.height});
                }
            }
        };
        
        updateSize();
        
        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(containerRef.current);
        
        return () => resizeObserver.disconnect();
    }, []);
    
    const data = useMemo(()=>points.map(p=>p.value), [points]);
    const positions = useMemo(() => {
        if (layout === 'packed') {
            return getPackedPositions(data, containerSize);
        }
        return getPositions(data, containerSize);
    }, [data, containerSize, layout]);
    const colors = useMemo(()=>data.map(it=>genColor()), [data]);
    return <div ref={containerRef} className="bubbles">
        {positions.map((it, i)=>{
            return <div key={`bubble-${i}`} className="bubble" 
                style={{
                    width: `${it.radius}px`, 
                    height: `${it.radius}px`,
                    top:`${it.position.y}px`,
                    left:`${it.position.x}px`, 
                    backgroundColor: colors[i]
                }}
                onClick={()=>{
                    onSelect(points[i].label);
                }}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                        position: { top: e.clientY, left: e.clientX },
                        wordIndex: i
                    });
                }}
                >
                {<AutoSizedText text={points[i].label} width={it.radius * 0.9 - 5} className="bubble_text"></AutoSizedText>}
            </div>
        })}
        
        {contextMenu && containerRef.current && (
            <CommonContextMenu 
                position={contextMenu.position}
                boundingElement={containerRef.current}
                onClose={() => setContextMenu(null)}
            >
                <div 
                    className="button" 
                    onClick={() => {
                        if (onDelete) {
                            onDelete(points[contextMenu.wordIndex].label);
                        }
                        setContextMenu(null);
                    }}
                    style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    <span style={{ fontSize: '16px', lineHeight: '1' }}>×</span>
                    {translate('admin_report_delete')}
                </div>
            </CommonContextMenu>
        )}
    </div>
}